import { FIELD, modeZoom } from "@golazo/shared";
import { FIT_HEIGHT, FIT_WIDTH, type FrameRect } from "../render/cameraFrame.js";
import { isMobileHandheld } from "./detect.js";
import { cameraFitsField, cameraZoomFactor } from "../render/cameraModes.js";

export interface PlayViewport {
  /** Escala de juego: encaje a pantalla x acercamiento del modo. */
  scale: number;
  /** Escala a la que cabria el campo entero. `scale / fitScale` = acercamiento. */
  fitScale: number;
  /** Rectangulo de pantalla donde vive el campo (ya descontado el HUD). */
  rect: FrameRect;
  /** HUD compacto y cámara priorizan ancho en móvil horizontal. */
  compactHud: boolean;
  hudTop: number;
}

const WORLD_WIDTH = FIELD.WIDTH + FIELD.MARGIN * 2;
const WORLD_HEIGHT = FIELD.HEIGHT + FIELD.MARGIN * 2;

/**
 * Franja que el HUD se reserva ARRIBA... y que ya no le quita al campo.
 *
 * Antes esto se descontaba del alto util: el cesped empezaba 79-119 px mas
 * abajo para que el minimapa no lo tapara. En un movil en horizontal (390 px de
 * alto) eso son 20 puntos porcentuales de campo regalados a una franja negra, y
 * el alto es justo el eje escaso — el campo se encaja por ancho. Se veian dos
 * bandas oscuras, arriba y abajo, estrangulando el verde.
 *
 * Ahora el campo ocupa la pantalla entera y el HUD flota ENCIMA, translucido.
 * El numero sigue existiendo porque el HUD lo necesita para colocarse (y para
 * que la camara sepa que hay una zona con adornos), pero ya no encoge el juego.
 */
const DESKTOP_RADAR_BAND = 119;
const COMPACT_RADAR_BAND = 79;

/** ¿Pantalla de mano, o ventana tan baja que se comporta como una? */
/** La misma regla que usa el HUD para ir en compacto (ver `scoreBugClearance`). */
export function isCompact(screenWidth: number, screenHeight: number): boolean {
  return isMobileHandheld() || (screenWidth < 920 && screenHeight < 520);
}

/**
 * Donde acaba la zona de adornos de arriba. Ya no es una reserva: es una
 * referencia de colocacion para el HUD.
 */
export function hudTopInset(screenWidth: number, screenHeight: number): number {
  return isCompact(screenWidth, screenHeight) ? COMPACT_RADAR_BAND : DESKTOP_RADAR_BAND;
}

/**
 * El campo usa TODA la ventana, en movil y en escritorio.
 *
 * La regla vieja ("el HUD vive en una franja y el cesped debajo") se cambia por
 * la de cualquier juego de deportes: el campo llena la pantalla y los rotulos
 * se superponen con transparencia. Lo que evita que el HUD esconda el balon ya
 * no es dejarle un hueco, sino que el HUD se aparte solo (ver `hud.ts`: baja la
 * opacidad cuando el balon se le acerca).
 */
export function computePlayViewport(screenWidth: number, screenHeight: number): PlayViewport {
  const compactHud = isCompact(screenWidth, screenHeight);
  const hudTop = hudTopInset(screenWidth, screenHeight);
  const zoom = modeZoom() * cameraZoomFactor();
  const rect = { x: 0, y: 0, width: screenWidth, height: screenHeight };

  /*
   * Modo CAMPO: que quepa entero, pase lo que pase.
   *
   * Es el unico paso que NO es un multiplicador. `modeZoom()` cambia con el
   * modo —el jugador de 11v11 se dibuja mas pequeño que el de 2v2— asi que el
   * factor que hace caber el campo seria distinto en cada uno y no se puede
   * escribir como una constante. Aqui si se sabe: es exactamente el encaje.
   *
   * A cambio se ve la banda de estadio a los lados o arriba, segun la forma de
   * la ventana. No es un fallo: es lo que significa "que quepa todo" en una
   * pantalla con otra proporcion que el campo.
   */
  if (cameraFitsField()) {
    const fitScale = Math.min(screenWidth / FIT_WIDTH, screenHeight / FIT_HEIGHT);
    return { scale: fitScale, fitScale, rect, compactHud, hudTop };
  }

  if (!compactHud) {
    const fitScale = Math.min(screenWidth / FIT_WIDTH, screenHeight / FIT_HEIGHT);
    return { scale: fitScale * zoom, fitScale, rect, compactHud: false, hudTop };
  }

  // La rama movil prioriza ancho y acepta recorte vertical, que es una
  // decision distinta (y no la puedo probar en un telefono de verdad).
  const fitScale = screenWidth / WORLD_WIDTH;
  return { scale: fitScale * zoom, fitScale, rect, compactHud: true, hudTop };
}

export { WORLD_WIDTH, WORLD_HEIGHT };
